// src/pages/EmployeeResignation.tsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getResignations, withdrawResignation } from '../../api/separation';
import ResignationSubmitModal from '../../components/separation/resignationsubmitmodal';

const EmployeeResignation: React.FC = () => {
  const { user } = useAuth();
  const [myResignations, setMyResignations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [selectedResignation, setSelectedResignation] = useState<any>(null);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);

  const loadMyResignations = async () => {
    setLoading(true);
    try {
      const response = await getResignations({ page: 1, limit: 10 });
      setMyResignations(response.data || []);
    } catch (error) {
      console.error('Failed to load resignations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMyResignations();
  }, []);

  const handleWithdraw = async () => {
    if (!selectedResignation) return;
    try {
      await withdrawResignation(selectedResignation.id, { reason: 'User requested withdrawal' });
      await loadMyResignations();
      setShowWithdrawConfirm(false);
      setSelectedResignation(null);
    } catch (error: any) {
      alert(error.message);
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      MANAGER_APPROVED: 'bg-blue-100 text-blue-800',
      MANAGER_REJECTED: 'bg-red-100 text-red-800',
      HR_APPROVED: 'bg-purple-100 text-purple-800',
      HR_REJECTED: 'bg-red-100 text-red-800',
      ADMIN_APPROVED: 'bg-green-100 text-green-800',
      ADMIN_REJECTED: 'bg-red-100 text-red-800',
      COMPLETED: 'bg-green-100 text-green-800',
      WITHDRAWN: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getNextApprover = (status: string) => {
    switch(status) {
      case 'PENDING': return 'Manager';
      case 'MANAGER_APPROVED': return 'HR';
      case 'HR_APPROVED': return 'Admin';
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                My Resignation
              </h1>
              <p className="text-gray-500 mt-1">Submit and track your resignation request</p>
            </div>
            
            <button
              onClick={() => setIsSubmitModalOpen(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 shadow-md transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Submit Resignation
            </button>
          </div>
        </div>

        {/* Active Resignation Info */}
        {myResignations.filter(r => !['WITHDRAWN', 'ADMIN_REJECTED', 'HR_REJECTED', 'MANAGER_REJECTED'].includes(r.status)).length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-xl">📋</span>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-blue-800">Active Resignation Request</h4>
                <p className="text-sm text-blue-700 mt-1">
                  You have submitted a resignation request. It is currently under review.
                  You can withdraw it if needed.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Resignation History */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Resignation History</h3>
            <p className="text-sm text-gray-500 mt-1">Track all your resignation requests</p>
          </div>

          <div className="divide-y divide-gray-100">
            {myResignations.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">📝</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">No Resignation Records</h4>
                <p className="text-gray-500 text-sm">
                  You haven't submitted any resignation requests yet.
                  Click the "Submit Resignation" button to get started.
                </p>
              </div>
            ) : (
              myResignations.map((r) => (
                <div key={r.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start flex-wrap gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(r.status)}`}>
                          {r.status}
                        </span>
                        {getNextApprover(r.status) && (
                          <span className="text-xs text-gray-500">
                            Waiting for: {getNextApprover(r.status)}
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                        <div>
                          <p className="text-xs text-gray-500">Resignation Date</p>
                          <p className="text-sm font-medium">
                            {r.resignation_date ? new Date(r.resignation_date).toLocaleDateString() : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Last Working Day</p>
                          <p className="text-sm font-medium">
                            {r.requested_last_day ? new Date(r.requested_last_day).toLocaleDateString() : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Notice Period</p>
                          <p className="text-sm font-medium">{r.notice_period_days} days</p>
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <p className="text-xs text-gray-500">Reason</p>
                        <p className="text-sm text-gray-700">{r.reason}</p>
                      </div>
                      
                      {r.manager_remarks && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500">Manager Feedback</p>
                          <p className="text-sm text-gray-700">{r.manager_remarks}</p>
                        </div>
                      )}
                    </div>
                    
                    {r.status === 'PENDING' && (
                      <button
                        onClick={() => {
                          setSelectedResignation(r);
                          setShowWithdrawConfirm(true);
                        }}
                        className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        Withdraw Request
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Submit Modal */}
      <ResignationSubmitModal
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
        onSuccess={() => {
          setIsSubmitModalOpen(false);
          loadMyResignations();
        }}
      />

      {/* Withdraw Confirmation Modal */}
      {showWithdrawConfirm && selectedResignation && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={() => setShowWithdrawConfirm(false)}></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-4">Withdraw Resignation</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to withdraw your resignation request? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleWithdraw}
                  className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Yes, Withdraw
                </button>
                <button
                  onClick={() => setShowWithdrawConfirm(false)}
                  className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeResignation;